import {InfiniteData, useInfiniteQuery, UseInfiniteQueryResult} from '@tanstack/react-query';
import {benefactorsApi} from '../api';
import {PointsAssignmentFilter, PointsAssignmentWithMetaModel} from '../model';
import {useCallback, useMemo, useState} from 'react';

type PointsAssignmentState = {
  getInfinitePointsAssignment: UseInfiniteQueryResult<InfiniteData<PointsAssignmentWithMetaModel>, Error>;
  filters: PointsAssignmentFilter;
  updateFilter: (newFilter: Partial<PointsAssignmentFilter>) => void;
};

const DEFAULT_PAGE_SIZE = 25;

export const useBenefactorPointsAssignment = (
  benefactorId: string,
  initialFilter: PointsAssignmentFilter = {},
): PointsAssignmentState => {
  const [filters, setFilters] = useState<PointsAssignmentFilter>(() => ({
    pageSize: DEFAULT_PAGE_SIZE,
    ...initialFilter,
  }));

  const infiniteQueryFilters = useMemo(() => {
    const {after, ...rest} = filters as any;
    return rest;
  }, [filters]);

  const getInfinitePointsAssignment = useInfiniteQuery<
    PointsAssignmentWithMetaModel,
    Error,
    InfiniteData<PointsAssignmentWithMetaModel>,
    ['benefactor-points-assignment', string, 'infinite', Omit<PointsAssignmentFilter, 'after'>],
    string | undefined
  >({
    queryKey: ['benefactor-points-assignment', benefactorId, 'infinite', infiniteQueryFilters],
    queryFn: ({pageParam}) => {
      return benefactorsApi.getBenefactorPointsAssignment(benefactorId, {
        ...infiniteQueryFilters,
        pageSize: filters.pageSize,
        after: pageParam,
      });
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage, _allPages, _lastPageParam) => {
      return lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined;
    },
    staleTime: 60 * 1000,
    enabled: !!benefactorId,
  });

  const updateFilter = useCallback((newFilter: Partial<PointsAssignmentFilter>) => {
    setFilters((prev) => {
      return {
        ...prev,
        ...newFilter,
      };
    });
  }, []);

  return {
    getInfinitePointsAssignment,
    filters,
    updateFilter,
  };
};
